import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Data, DataLabel } from '../styles';
import { Pie } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

function formatPercentage(decimal: number): string {
  return `${decimal < 10 ? ' ' : ''}${decimal}%${decimal % 0.1 === 0 ? ' ' : ''}`;
}

function formatText(text: string, lines: number): string {
  // if the text is not lines long, add "\n" until it is
  while (text.split('\n').length < lines) {
    text += '\n';
  }
  return text;
}

const PieChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const LegendContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  @media (min-width: 768px) {
    width: 33.33%;
  }
`;

const PieContainer = styled.div`
  width: 100%;
  max-height: 700px;
  display: flex;
  justify-content: center;
`;

interface Props {
  data: number[];
  sliceNames: string[];
  show_legend?: boolean;
  show_percentages?: boolean;
  hover_num: number;
  show_leg_percentage?: boolean;
  show_legend_first?: boolean;
}

Chart.register(ChartDataLabels);

const PieChart: React.FC<Props> = (props: Props) => {
  // if we want the side-legend, this is the index we display
  const [displayDataIndex, setDisplayDataIndex] = useState<number>(0);

  // make an array colors like '#e6f2ff' * props.data.length
  // let colors = Array(props.data.length).fill('#CF9BBD');

  const colors = [
    '#F0BDCA',
    '#E496B6',
    '#CF9BBD',
    '#D770A1',
    '#FDE3DF',
    '#B977AC',
    '#A2539B',
    '#E6BFCE',
    '#8B2F8A',
    '#CA498C',
  ];
  // contains names
  let data = {
    labels: props.sliceNames.map(sliceNames => {
      return sliceNames.split(' ');
    }),
    datasets: [
      {
        backgroundColor: colors,
        hoverBackgroundColor: colors,
        data: props.data,
        hoverOffset: props.hover_num,
        borderRadius: 10,
        borderSkipped: false,
      },
    ],
  };
  return (
    <PieChartContainer style={{ display: 'flex' }}>
      <LegendContainer>
        {props.show_leg_percentage ? (
          <Data style={{ color: 'black', fontSize: '64px' }}>{formatPercentage(props.data[displayDataIndex])}</Data>
        ) : null}
        {props.sliceNames[displayDataIndex] && (
          <DataLabel>{formatText(props.sliceNames[displayDataIndex].replace('/', ' '), 4)}</DataLabel>
        )}
      </LegendContainer>
      <PieContainer style={{ width: '100%', maxHeight: '700px' }}>
        <Pie
          data={data}
          options={{
            layout: {
              padding: 60,
            },
            responsive: true,
            onHover(_, elements) {
              if (elements[0]?.index !== undefined) {
                setDisplayDataIndex(elements[0]?.index);
              }
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: false,
                callbacks: {
                  label: function (context: any) {
                    return props.data[context.dataIndex] + '%';
                  },
                  title: function (context: any) {
                    return props.sliceNames[context[0].dataIndex];
                  },
                },
                displayColors: false,
              },
              datalabels: props.show_percentages
                ? {
                    align: 'center',
                    color: 'black',
                    anchor: 'center',
                    formatter: function (value: number) {
                      return value + '%';
                    },
                    font: function (context) {
                      var width = context.chart.width;
                      var size = Math.round(width / 32);

                      return {
                        weight: 700,
                        size: size,
                        family: 'Work Sans',
                      };
                    },
                  }
                : {
                    display: props.show_percentages,
                  },
            },
          }}
        />
      </PieContainer>
    </PieChartContainer>
  );
};

export default PieChart;
